import HeroSection from "./components/HeroSection";
import WhatIsExpeditingSection from "./components/WhatIsExpeditingSection";
import HowToVerifySection from "./components/HowToVerifySection";
import HowItWorksSection from "./components/HowItWorksSection";
import WhyFpcSection from "./components/WhyFpcSection";
import ServicesExpediteSection from "./components/ServicesExpediteSection";
import ProcessingSpeedsSection from "./components/ProcessingSpeedsSection";
import FAQSection, { faqs } from "./components/FAQSection";
import FinalCTASection from "./components/FinalCTASection";
import RelatedPassportServices from "@/components/feature/RelatedPassportServices";

const pageUrl = "https://fastpassportcenter.com/passport-services/passport-expediting-service/";

const faqSchemaItems = faqs.map((faq) => ({
  "@type": "Question",
  name: faq.question,
  acceptedAnswer: {
    "@type": "Answer",
    text: faq.answer,
  },
}));

const serviceSchema = {
  "@type": "Service",
  name: "Passport Expediting Service",
  provider: {
    "@type": "Organization",
    name: "Fast Passport Center",
    url: "https://fastpassportcenter.com",
    telephone: "1-877-253-0084",
    address: {
      "@type": "PostalAddress",
      streetAddress: "1629 K Street, Suite 300",
      addressLocality: "Washington",
      addressRegion: "DC",
      postalCode: "20006",
    },
  },
  description: "Registered US Department of State passport expediting service. Direct courier. Processing from 3 business days. A+ BBB, 20+ years.",
  areaServed: "US",
  offers: {
    "@type": "Offer",
    price: "195",
    priceCurrency: "USD",
    description: "Passport expediting service from $195",
  },
};

const breadcrumbSchema = {
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: "https://fastpassportcenter.com" },
    { "@type": "ListItem", position: 2, name: "Passport Services", item: "https://fastpassportcenter.com/passport-services/" },
    { "@type": "ListItem", position: 3, name: "Passport Expediting Service", item: pageUrl },
  ],
};

const graph = [
  {
    "@type": "Organization",
    name: "Fast Passport Center",
    url: "https://fastpassportcenter.com",
    telephone: "+1-877-253-0084",
    email: "contactus@fastpassportcenter.com",
    address: {
      "@type": "PostalAddress",
      streetAddress: "1629 K Street, Suite 300",
      addressLocality: "Washington",
      addressRegion: "DC",
      postalCode: "20006",
      addressCountry: "US",
    },
    areaServed: { "@type": "Country", name: "United States" },
    priceRange: "$$$",
    openingHours: ["Mo-Fr 09:00-18:00"],
    aggregateRating: { "@type": "AggregateRating", ratingValue: "4.9", reviewCount: "1200" },
  },
  serviceSchema,
  breadcrumbSchema,
  { "@type": "FAQPage", mainEntity: faqSchemaItems },
];

const jsonLd = JSON.stringify({ "@context": "https://schema.org", "@graph": graph });

function PassportExpeditingServiceSEOHead() {
  return (
    <>
      <title>Passport Expediting Service — Registered State Dept Courier | Fast Passport Center</title>
      <meta name="description" content="Fast Passport Center is a registered US Department of State passport expediting service. Direct courier — not a reseller. A+ BBB, 20+ years. Processing from 3 business days. 20–80% less than competitors." />
      <meta name="keywords" content="passport expediting service, passport expediter, expedited passport service, registered passport courier, expedite passport, passport courier service, fast passport" />
      <meta name="last-modified" content="2026-07-01" />
      <meta property="og:type" content="website" />
      <meta property="og:title" content="Passport Expediting Service — Registered State Dept Courier | Fast Passport Center" />
      <meta property="og:description" content="Registered US Department of State passport expediting service. Direct courier — not a reseller. A+ BBB, 20+ years. Processing from 3 business days." />
      <meta property="og:url" content={pageUrl} />
      <meta property="og:site_name" content="Fast Passport Center" />
      <meta property="og:image" content="https://fastpassportcenter.com/og-image.jpg" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="Passport Expediting Service — Registered State Dept Courier | Fast Passport Center" />
      <meta name="twitter:description" content="Registered US Department of State passport expediting service. Direct courier — not a reseller. A+ BBB, 20+ years. Processing from 3 business days." />
      <meta name="twitter:image" content="https://fastpassportcenter.com/og-image.jpg" />
      <link rel="canonical" href={pageUrl} />
      <script type="application/ld+json" id="structured-data-passport-expediting-service" dangerouslySetInnerHTML={{ __html: jsonLd }} />
    </>
  );
}

export default function PassportExpeditingServicePage() {
  return (
    <div className="font-sans">
      <PassportExpeditingServiceSEOHead />
      <HeroSection />
      <WhatIsExpeditingSection />
      <HowToVerifySection />
      <HowItWorksSection />
      <WhyFpcSection />
      <ServicesExpediteSection />
      <ProcessingSpeedsSection />
      <FAQSection />

      <RelatedPassportServices
        services={[
          {
            title: "Expedited Passport Service",
            description: "Compare all speed tiers side-by-side. Pick the processing speed that fits your travel date.",
            link: "/passport-services/expedited-passport-service/",
            icon: "ri-speed-up-line",
          },
          {
            title: "Fast Passport Renewal",
            description: "The easiest path if you're renewing. No office visit needed for eligible renewals.",
            link: "/passport-services/fast-passport-renewal/",
            icon: "ri-refresh-line",
          },
          {
            title: "Urgent Passport Service",
            description: "Not sure what timeline you're working with? See what's possible at each urgency level.",
            link: "/passport-services/urgent-passport/",
            icon: "ri-timer-line",
          },
          {
            title: "Emergency Passport",
            description: "Need it in less than 72 hours? Emergency processing as fast as 24 hours. Call now.",
            link: "/passport-services/emergency-passport/",
            icon: "ri-alert-line",
          },
        ]}
        colorClass="text-[#1A365D]"
        bgClass="bg-[#F0F4FA]"
        borderClass="border-[#D4DFED]"
        linkColorClass="text-[#1A365D]"
      />

      <FinalCTASection />

      {/* Footer */}
      <footer className="bg-[#1A365D] text-gray-300 py-12 md:py-16">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 mb-10">
            <div>
              <h4 className="text-white font-bold text-sm uppercase tracking-wider mb-4">Fast Passport Center</h4>
              <p className="text-gray-400 text-sm leading-relaxed mb-3">
                Registered U.S. Department of State passport courier. A+ BBB rated. 20+ years of passport and visa experience.
              </p>
              <p className="text-gray-500 text-xs">
                1629 K Street, Suite 300<br />Washington, DC 20006
              </p>
            </div>
            <div>
              <h4 className="text-white font-bold text-sm uppercase tracking-wider mb-4">Services</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href="/passport-services/passport-expediting-service/" className="hover:text-white transition-colors cursor-pointer">Passport Expediting Service</a></li>
                <li><a href="/passport-services/fast-passport-renewal/" className="hover:text-white transition-colors cursor-pointer">Fast Passport Renewal</a></li>
                <li><a href="/passport-services/expedited-passport-service/" className="hover:text-white transition-colors cursor-pointer">Expedited Passport Service</a></li>
                <li><a href="/passport-services/emergency-passport/" className="hover:text-white transition-colors cursor-pointer">Emergency Passport</a></li>
                <li><a href="/passport-services/urgent-passport/" className="hover:text-white transition-colors cursor-pointer">Urgent Passport</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-bold text-sm uppercase tracking-wider mb-4">Support</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href="tel:8772530084" className="hover:text-white transition-colors cursor-pointer whitespace-nowrap">877-253-0084</a></li>
                <li><a href="mailto:contactus@fastpassportcenter.com" className="hover:text-white transition-colors cursor-pointer">Contact Us</a></li>
                <li><a href="/helpful-resources/" className="hover:text-white transition-colors cursor-pointer">Helpful Resources</a></li>
                <li><a href="/helpful-resources/passport-processing-times/" className="hover:text-white transition-colors cursor-pointer">Processing Times</a></li>
                <li><a href="/helpful-resources/passport-fees/" className="hover:text-white transition-colors cursor-pointer">Passport Fees</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-bold text-sm uppercase tracking-wider mb-4">Trust &amp; Credentials</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li className="flex items-center gap-2">
                  <i className="ri-shield-check-line"></i> A+ BBB Accredited
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-government-fill"></i> Registered State Dept. Courier
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-history-line"></i> 20+ Years Experience
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-lock-line"></i> Secure &amp; Confidential
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-[#4A7AB5]/30 pt-8 text-center text-xs text-gray-500">
            <p>&copy; {new Date().getFullYear()} Fast Passport Center. All rights reserved. Fast Passport Center is a private passport courier service registered with the U.S. Department of State. We are not a government agency.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}